import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, TextField, } from "@mui/material";
import { AutocompleteComponent } from "components/Autocomplete/AutocompleteComponent";
import { ModalV2 } from "components/Modal/ModalV2";
import { getSelfAndDescendantAccountIds } from "./accountingAccountHelpers";
import {
  accountTypeOptions, currencyOptions, DEFAULT_OPTIONS,
  ledgerAccountOptions, booleanOptions, ACCOUNT_MAX_CHAR,
} from "./accountingAccountOptions";
import { selectAccountingAccountTableData } from "./accountingAccountSelectors";
import { getFieldErrorProps } from "helpers/formFieldProps";
import { orderArrayV2 } from "helpers/orderArray";
import { useForm } from "hooks/useForm";
import { createAccountingAccount, updateAccountingAccount } from "redux/actions/accountingAccount/accountingAccountActions";
import { ACCOUNTING_ACCOUNT_LOADINGS } from "redux/reducers/accountingAccount/accountingAccountTypes";

// Tipos en orden alfabético; se ordena una copia porque orderArrayV2 muta el
// arreglo recibido.
const sortedAccountTypeOptions = orderArrayV2([...accountTypeOptions], "label");

const accountingAccountValidations = {
  selectedStation: { required: true },
  number: { required: true, maxLength: ACCOUNT_MAX_CHAR },
  selectedAccountType: { required: true },
  name: { required: true, maxLength: 150 },
  selectedLedgerAccount: { required: true },
  selectedCurrency: { required: true },
  level: { required: true },
  selectedSatCode: { required: true },
};

const { CREATE, UPDATE } = ACCOUNTING_ACCOUNT_LOADINGS;

const AccountingAccountForm = ({
  data = {},
  open = false,
  setOpen = () => {},
  titlePrefix = "Nueva",
  update = false,
}) => {
  const dispatch = useDispatch();
  const stationsList = useSelector(({ companies }) => companies.stationsList);
  const { satGroupingCodesList, station } = useSelector(({ accountingAccount }) => accountingAccount);
  const accountingAccountList = useSelector(selectAccountingAccountTableData);
  const isLoadingComp = useSelector(({ search }) => search.isLoadingComp);

  const loading = isLoadingComp?.[CREATE] || isLoadingComp?.[UPDATE] || false;
  const targetAccountType = data.accountType || DEFAULT_OPTIONS.ACCOUNT_TYPE;
  const targetLedgerAccount = data.ledgerAccount || DEFAULT_OPTIONS.LEDGER_ACCOUNT;
  const targetCurrency = data.currency || DEFAULT_OPTIONS.CURRENCY;
  const excludedParentIds = getSelfAndDescendantAccountIds(accountingAccountList, data.id);
  const parentAccountOptions = accountingAccountList.filter(
    (acc) => !excludedParentIds.has(acc.id)
  );

  const initialValues = {
    selectedStation: station || null,
    number: data.number || "",
    selectedAccountType: accountTypeOptions.find((acc) => acc.value === targetAccountType) || null,
    name: data.name || "",
    selectedParentAccount: accountingAccountList.find((acc) => acc.id === data.parentAccount) || null,
    selectedLedgerAccount: ledgerAccountOptions.find((acc) => acc.value === targetLedgerAccount) || null,
    selectedCurrency: currencyOptions.find((curr) => curr.value === targetCurrency) || null,
    level: data.level || "",
    selectedLastLevel: booleanOptions.find((level) => level.value === data.lastLevel) || null,
    selectedAffectsSpending: booleanOptions.find((affects) => affects.value === data.affectsSpending) || null,
    selectedSatCode: satGroupingCodesList.find((code) => code.id === data.idGrouperCodeSat) || null,
  };

  const {
    values, onChange, setValues, errors, validate,
  } = useForm(initialValues, accountingAccountValidations);

  const {
    selectedStation,
    number,
    selectedAccountType,
    name,
    selectedParentAccount,
    selectedLedgerAccount,
    selectedCurrency,
    level,
    selectedLastLevel,
    selectedAffectsSpending,
    selectedSatCode,
  } = values;

  const handleSanitizedField = (regex) => (e) => {
    const { name: fieldName, value } = e.target;
    onChange({ target: { name: fieldName, value: value.replace(regex, "") } });
  };

  const handleNumberField = handleSanitizedField(/[^0-9/-]/g);
  const handleNameField = handleSanitizedField(/[*@]/g);
  const handleLevelField = handleSanitizedField(/[^0-9]/g);

  const handleSubmit = () => {
    validate([], () => {
      const form = {
        idStation: selectedStation.id,
        number,
        accountType: selectedAccountType.value,
        name,
        parentAccount: selectedParentAccount?.id || null,
        ledgerAccount: selectedLedgerAccount.value,
        currency: selectedCurrency.value,
        level: Number(level),
        lastLevel: selectedLastLevel?.value ?? null,
        affectsSpending: Boolean(selectedAffectsSpending?.value ?? false),
        idGrouperCodeSat: selectedSatCode.value,
      };
      if (update) {
        dispatch(updateAccountingAccount(data.id, form, setOpen));
      } else {
        dispatch(createAccountingAccount(form, setOpen));
      }
    });
  };

  return (
    <ModalV2
      title={`${titlePrefix} Cuenta Contable`}
      open={open}
      onClose={setOpen}
      onSubmit={handleSubmit}
      submitBtnText={update ? "Actualizar" :"Crear"}
      toRight
      isLoading={loading}
    >
      <Grid item xs={12}>
        <AutocompleteComponent
          label="Planta"
          name="selectedStation"
          value={selectedStation}
          handleValues={setValues}
          fullWidth
          variant="outlined"
          textFieldProps={getFieldErrorProps(errors, "selectedStation")}
          options={stationsList}
          disabled
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="Número"
          name="number"
          value={number}
          onChange={handleNumberField}
          fullWidth
          variant="outlined"
          {...getFieldErrorProps(errors, "number")}
        />
      </Grid>
      <Grid item xs={12} >
        <AutocompleteComponent
          label="Tipo"
          name="selectedAccountType"
          value={selectedAccountType}
          handleValues={setValues}
          fullWidth
          variant="outlined"
          textFieldProps={getFieldErrorProps(errors, "selectedAccountType")}
          options={sortedAccountTypeOptions}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="Nombre"
          name="name"
          value={name}
          onChange={handleNameField}
          fullWidth
          variant="outlined"
          {...getFieldErrorProps(errors, "name")}
        />
      </Grid>
      <Grid item xs={12}>
        <AutocompleteComponent
          label="Cuenta Padre"
          name="selectedParentAccount"
          value={selectedParentAccount}
          handleValues={setValues}
          fullWidth
          variant="outlined"
          textFieldProps={getFieldErrorProps(errors, "selectedParentAccount")}
          options={parentAccountOptions}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <AutocompleteComponent
          label="Cuenta Mayor"
          name="selectedLedgerAccount"
          value={selectedLedgerAccount}
          handleValues={setValues}
          fullWidth
          variant="outlined"
          textFieldProps={getFieldErrorProps(errors, "selectedLedgerAccount")}
          options={ledgerAccountOptions}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <AutocompleteComponent
          label="Tipo de Moneda"
          name="selectedCurrency"
          value={selectedCurrency}
          handleValues={setValues}
          fullWidth
          variant="outlined"
          textFieldProps={getFieldErrorProps(errors, "selectedCurrency")}
          options={currencyOptions}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          label="Nivel Interno"
          name="level"
          value={level}
          onChange={handleLevelField}
          fullWidth
          variant="outlined"
          {...getFieldErrorProps(errors, "level")}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <AutocompleteComponent
          label="Último Nivel"
          name="selectedLastLevel"
          value={selectedLastLevel}
          handleValues={setValues}
          fullWidth
          variant="outlined"
          textFieldProps={getFieldErrorProps(errors, "selectedLastLevel")}
          options={booleanOptions}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <AutocompleteComponent
          label="Afectable"
          name="selectedAffectsSpending"
          value={selectedAffectsSpending}
          handleValues={setValues}
          fullWidth
          variant="outlined"
          textFieldProps={getFieldErrorProps(errors, "selectedAffectsSpending")}
          options={booleanOptions}
        />
      </Grid>
      <Grid item xs={12}>
        <AutocompleteComponent
          label="Código Agrupador (SAT)"
          name="selectedSatCode"
          value={selectedSatCode}
          handleValues={setValues}
          fullWidth
          variant="outlined"
          textFieldProps={getFieldErrorProps(errors, "selectedSatCode")}
          options={satGroupingCodesList}
        />
      </Grid>
    </ModalV2>
  );
};

export default AccountingAccountForm;
